import { useState } from "react";
import { SHELL, Reveal, Mono } from "@/components/bits";
import { DOC_TOPICS } from "@/lib/docsData";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";

function SectionBlock({ s, tid }) {
    return (
        <div id={tid} className="scroll-mt-28 mb-10" data-testid={`docs-section-${tid}`}>
            <h3 className="text-lg font-bold tracking-wide mb-4 text-vera-white">
                <span className="text-vera-gold mr-3 font-mono text-xs align-middle">§</span>
                {s.h}
            </h3>
            {s.type === "p" && <p className="text-sm text-vera-mut leading-relaxed">{s.text}</p>}
            {s.type === "list" && (
                <ul className="space-y-2.5">
                    {s.items.map((it) => (
                        <li key={it} className="flex items-start gap-3 text-sm text-vera-mut leading-relaxed">
                            <span className="w-1 h-1 rotate-45 bg-vera-gold mt-2 shrink-0" aria-hidden="true" />
                            {it}
                        </li>
                    ))}
                </ul>
            )}
            {s.type === "table" && (
                <div className="overflow-x-auto border border-white/10">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-white/10">
                                {s.head.map((h) => (
                                    <th key={h} className="font-mono text-[9px] tracking-[0.2em] text-vera-gold px-4 py-3">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {s.rows.map((r, i) => (
                                <tr key={i} className="border-b border-white/5 last:border-0">
                                    {r.map((cell, j) => (
                                        <td key={j} className={`px-4 py-3 text-xs leading-relaxed ${j === 0 ? "font-mono text-vera-white/90" : "text-vera-mut"}`}>{cell}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {s.type === "code" && (
                <pre className="bg-vera-bg border border-white/10 p-5 overflow-x-auto font-mono text-[11px] leading-6 text-vera-soft" data-testid={`docs-code-${tid}`}>{s.text}</pre>
            )}
            {s.type === "callout" && (
                <div className={`border-l-2 px-5 py-4 ${s.tone === "gold" ? "border-vera-gold bg-vera-gold/5" : "border-[#F59E0B] bg-[#F59E0B]/5"}`}>
                    <p className={`font-mono text-[9px] tracking-[0.25em] mb-2 ${s.tone === "gold" ? "text-vera-gold2" : "text-[#F59E0B]"}`}>{s.title}</p>
                    <p className="text-sm text-vera-mut leading-relaxed">{s.text}</p>
                </div>
            )}
            {s.type === "links" && (
                <div className="space-y-2.5">
                    {s.links.map((l) =>
                        l.url.startsWith("/") ? (
                            <Link key={l.label} to={l.url} className="flex items-center gap-2 text-sm text-vera-soft hover:text-white transition-colors">
                                {l.label} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                            </Link>
                        ) : (
                            <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-vera-soft hover:text-white transition-colors">
                                {l.label} <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                            </a>
                        )
                    )}
                </div>
            )}
        </div>
    );
}

export default function Docs() {
    const [topicId, setTopicId] = useState(DOC_TOPICS[0].id);
    const topic = DOC_TOPICS.find((t) => t.id === topicId) || DOC_TOPICS[0];

    return (
        <div data-testid="docs-page">
            <section className="pt-32 pb-10 border-b border-white/10">
                <SHELL>
                    <Reveal y={14}>
                        <div className="flex items-center gap-4 mb-4">
                            <span className="w-1.5 h-1.5 rounded-full bg-vera-gold" aria-hidden="true" />
                            <Mono className="text-vera-mut">VERA DOCUMENTATION</Mono>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Institutional-grade documentation for the VERA protocol.</h1>
                    </Reveal>
                </SHELL>
            </section>

            <section className="py-12 md:py-16">
                <SHELL>
                    <div className="grid lg:grid-cols-[240px_1fr_190px] gap-10">
                        <aside className="lg:sticky lg:top-24 lg:self-start max-lg:order-1">
                            <Mono className="text-vera-mut block mb-4 max-lg:hidden">TOPICS</Mono>
                            <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                                {DOC_TOPICS.map((t) => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setTopicId(t.id)}
                                        data-testid={`docs-topic-${t.id}`}
                                        className={`flex items-center gap-3 shrink-0 lg:w-full text-left px-4 py-2.5 border transition-all ${t.id === topicId ? "border-vera-gold/50 text-vera-gold2 bg-vera-gold/10" : "border-white/10 text-vera-mut hover:text-white hover:border-white/20"}`}
                                    >
                                        <span className="font-mono text-[9px]">{t.num}</span>
                                        <span className="font-mono text-[10px] tracking-[0.15em] whitespace-nowrap">{t.title.toUpperCase()}</span>
                                    </button>
                                ))}
                            </div>
                        </aside>

                        <main className="min-w-0 max-lg:order-2" data-testid="docs-content">
                            <div className="flex items-center gap-3 mb-3">
                                <span className="font-mono text-[9px] tracking-[0.25em] text-vera-gold border border-vera-gold/40 px-2 py-1">{topic.tag}</span>
                                <span className="font-mono text-[9px] tracking-[0.25em] text-vera-mut">{topic.num} / 14</span>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-4">{topic.title}</h2>
                            <p className="text-sm sm:text-base text-vera-mut leading-relaxed mb-12 pb-10 border-b border-white/10">{topic.intro}</p>
                            {topic.sections.map((s, i) => (
                                <SectionBlock key={i} s={s} tid={`${topic.id}-${i}`} />
                            ))}
                        </main>

                        <aside className="hidden lg:block sticky top-24 self-start max-lg:order-3">
                            <Mono className="text-vera-mut block mb-4">ON THIS PAGE</Mono>
                            <ul className="space-y-2.5 border-l border-white/10 pl-4">
                                {topic.sections.map((s, i) => (
                                    <li key={i}>
                                        <button
                                            type="button"
                                            onClick={() => document.getElementById(`${topic.id}-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                                            className="text-left text-xs text-vera-mut hover:text-vera-gold2 transition-colors leading-snug"
                                        >
                                            {s.h}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </aside>
                    </div>
                </SHELL>
            </section>
        </div>
    );
}
